import React, { useMemo, useState, Children, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, ArrowUpRight, ArrowDownRight, Search, Download, CreditCard, Building, Receipt, FileText, CheckCircle2, Calendar, Smartphone, Hash, Wallet as WalletIcon } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { useAppContext, Transaction, User } from '../contexts/AppContext';
import { formatCurrency, formatDate, formatDateTime } from '../utils/formatters';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Modal } from '../components/ui/Modal';
import { EmptyState } from '../components/ui/EmptyState';
import { BillvastLogo } from '../components/ui/BillvastLogo';
import { toast } from 'sonner';
const COLORS = ['#73C883', '#FFB347', '#3b82f6', '#8b5cf6', '#f43f5e'];
// Signature names pool for random selection
const SIGNATURE_NAMES = ['A. Okonkwo', 'C. Adeyemi', 'F. Nwosu', 'B. Ibrahim', 'D. Afolabi', 'E. Chukwu', 'G. Balogun', 'H. Eze'];
const getRandomSignatureName = () => SIGNATURE_NAMES[Math.floor(Math.random() * SIGNATURE_NAMES.length)];
// SVG stamp + signature HTML for PDFs
const getStampAndSignatureHTML = () => {
  const sigName = getRandomSignatureName();
  return ['<div style="display:flex;justify-content:space-between;align-items:flex-end;margin-top:40px;padding:0 20px;">', '  <div style="text-align:center;">', '    <svg width="120" height="120" viewBox="0 0 120 120">', '      <circle cx="60" cy="60" r="56" fill="none" stroke="#006633" stroke-width="3" opacity="0.7"/>', '      <circle cx="60" cy="60" r="48" fill="none" stroke="#006633" stroke-width="1.5" opacity="0.5"/>', '      <text x="60" y="38" text-anchor="middle" font-size="8" font-weight="700" fill="#006633" letter-spacing="3" transform="rotate(-0,60,60)">BILLVAST</text>', '      <text x="60" y="55" text-anchor="middle" font-size="11" font-weight="800" fill="#006633">&#10003; VERIFIED</text>', '      <text x="60" y="70" text-anchor="middle" font-size="7" fill="#006633" opacity="0.7">FINTECH PLATFORM</text>', '      <text x="60" y="85" text-anchor="middle" font-size="6" fill="#006633" opacity="0.6">Lagos, Nigeria</text>', '      <path d="M 15 60 A 45 45 0 0 1 105 60" fill="none" stroke="#006633" stroke-width="0.5" opacity="0.3"/>', '      <path d="M 15 60 A 45 45 0 0 0 105 60" fill="none" stroke="#006633" stroke-width="0.5" opacity="0.3"/>', '    </svg>', '  </div>', '  <div style="text-align:center;">', '    <svg width="160" height="60" viewBox="0 0 160 60">', '      <path d="M10 45 C20 15, 35 10, 50 30 S70 50, 85 25 S105 5, 120 35 C130 45, 140 40, 150 30" fill="none" stroke="#1A2E1A" stroke-width="2" stroke-linecap="round" opacity="0.7"/>', '      <path d="M30 48 C40 42, 55 38, 70 44 S90 52, 110 42" fill="none" stroke="#1A2E1A" stroke-width="1" stroke-linecap="round" opacity="0.4"/>', '    </svg>', '    <div style="font-size:12px;font-weight:600;color:#1A2E1A;margin-top:4px;">' + sigName + '</div>', '    <div style="font-size:10px;color:#6B7280;">Authorized Signatory</div>', '    <div style="border-top:1px solid #d1d5db;width:140px;margin:6px auto 0;"></div>', '  </div>', '</div>'].join('\n');
};
// --- Helper Functions for Generation ---
const generateReceiptPDF = (tx: Transaction) => {
  const win = window.open('', '_blank');
  if (!win) {
    toast.error('Please allow popups to generate PDF');
    return;
  }
  const amountColor = tx.amount > 0 ? '#006633' : '#1A2E1A';
  const amountFormatted = (tx.amount > 0 ? '+' : '') + formatCurrency(tx.amount);
  const stampAndSig = getStampAndSignatureHTML();
  const html = ['<!DOCTYPE html>', '<html>', '<head>', '  <title>Receipt - ' + tx.reference + '</title>', '  <style>{`', '    @import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap");', '    * { box-sizing: border-box; margin: 0; padding: 0; }', '    body { font-family: "Inter", sans-serif; padding: 40px; color: #1A2E1A; max-width: 600px; margin: 0 auto; background: #fff; }', '    .header { text-align: center; margin-bottom: 30px; }', '    .logo { max-width: 140px; margin-bottom: 16px; }', '    .title { font-size: 20px; font-weight: 600; color: #1A2E1A; text-transform: uppercase; letter-spacing: 1px; }', '    .divider { border-top: 2px dashed #e5e7eb; margin: 24px 0; }', '    .amount-container { text-align: center; margin: 30px 0; }', '    .amount-label { color: #6B7280; font-size: 14px; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px; }', '    .amount { font-size: 40px; font-weight: 700; color: ' + amountColor + '; }', '    .row { display: flex; justify-content: space-between; margin-bottom: 16px; font-size: 15px; }', '    .label { color: #6B7280; }', '    .value { font-weight: 600; text-align: right; max-width: 60%; }', '    .status { color: #059669; font-weight: 600; display: flex; align-items: center; gap: 6px; justify-content: flex-end; }', '    .footer { text-align: center; margin-top: 30px; color: #6B7280; font-size: 13px; line-height: 1.6; }', '    @media print { body { padding: 20px; } * { -webkit-print-color-adjust: exact; } }', '`}</style>', '</head>', '<body>', '  <div class="header">', '    <img src="https://cdn.magicpatterns.com/uploads/4AEfCpc54BiEMtMYwDkd9o/Billvast_logo.png" class="logo" alt="Billvast" />', '    <h1 class="title">Transaction Receipt</h1>', '  </div>', '  <div class="amount-container">', '    <div class="amount-label">Amount</div>', '    <div class="amount">' + amountFormatted + '</div>', '  </div>', '  <div class="divider"></div>', '  <div class="row"><span class="label">Transaction Type</span><span class="value">' + tx.type + '</span></div>', '  <div class="row"><span class="label">Description</span><span class="value">' + tx.title + '</span></div>', '  <div class="row"><span class="label">Date &amp; Time</span><span class="value">' + formatDateTime(tx.date) + '</span></div>', '  <div class="row"><span class="label">Reference ID</span><span class="value" style="font-family:monospace">' + tx.reference + '</span></div>', '  <div class="row"><span class="label">Status</span><span class="value status">&#10003; ' + tx.status + '</span></div>', '  <div class="divider"></div>', stampAndSig, '  <div class="footer">', '    <strong>Thank you for using Billvast.</strong><br/>', '    This is a computer-generated receipt.<br/>', '    If you have any questions, please contact support@billvast.com', '  </div>', '  <script>window.onload = function() { setTimeout(function() { window.print(); }, 500); }<\/script>', '</body>', '</html>'].join('\n');
  win.document.write(html);
  win.document.close();
};
const generateReceiptPNG = (tx: Transaction) => {
  const canvas = document.createElement('canvas');
  canvas.width = 600;
  canvas.height = 950;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const sigName = getRandomSignatureName();
  // Background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, 600, 950);
  // Top accent bar
  ctx.fillStyle = '#73C883';
  ctx.fillRect(0, 0, 600, 6);
  // Logo text
  ctx.fillStyle = '#006633';
  ctx.font = 'bold 32px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('BILLVAST', 300, 70);
  ctx.fillStyle = '#1A2E1A';
  ctx.font = '600 16px Arial';
  ctx.fillText('TRANSACTION RECEIPT', 300, 100);
  // Amount section
  ctx.fillStyle = '#6B7280';
  ctx.font = '13px Arial';
  ctx.fillText('AMOUNT', 300, 160);
  ctx.fillStyle = tx.amount > 0 ? '#006633' : '#1A2E1A';
  ctx.font = 'bold 44px Arial';
  ctx.fillText((tx.amount > 0 ? '+' : '') + formatCurrency(tx.amount), 300, 215);
  // Dashed divider helper
  const drawDivider = (y: number) => {
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 2;
    ctx.setLineDash([8, 8]);
    ctx.beginPath();
    ctx.moveTo(50, y);
    ctx.lineTo(550, y);
    ctx.stroke();
    ctx.setLineDash([]);
  };
  drawDivider(260);
  // Details rows
  ctx.textAlign = 'left';
  const details = [{
    label: 'Transaction Type',
    value: tx.type
  }, {
    label: 'Description',
    value: tx.title
  }, {
    label: 'Date & Time',
    value: formatDateTime(tx.date)
  }, {
    label: 'Reference ID',
    value: tx.reference
  }, {
    label: 'Status',
    value: '\u2713 ' + tx.status
  }];
  const startY = 310;
  const lineHeight = 52;
  details.forEach((item, index) => {
    const y = startY + index * lineHeight;
    ctx.fillStyle = '#6B7280';
    ctx.font = '14px Arial';
    ctx.fillText(item.label, 50, y);
    ctx.fillStyle = item.label === 'Status' ? '#059669' : '#1A2E1A';
    ctx.font = item.label === 'Reference ID' ? '14px monospace' : 'bold 14px Arial';
    ctx.textAlign = 'right';
    ctx.fillText(item.value, 550, y);
    ctx.textAlign = 'left';
  });
  drawDivider(580);
  // --- Stamp (circular seal) ---
  const stampX = 130;
  const stampY = 680;
  const stampR = 55;
  ctx.save();
  ctx.globalAlpha = 0.7;
  // Outer circle
  ctx.strokeStyle = '#006633';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(stampX, stampY, stampR, 0, Math.PI * 2);
  ctx.stroke();
  // Inner circle
  ctx.lineWidth = 1.5;
  ctx.globalAlpha = 0.5;
  ctx.beginPath();
  ctx.arc(stampX, stampY, stampR - 8, 0, Math.PI * 2);
  ctx.stroke();
  // Stamp text
  ctx.globalAlpha = 0.8;
  ctx.fillStyle = '#006633';
  ctx.font = 'bold 9px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('BILLVAST', stampX, stampY - 18);
  ctx.font = 'bold 13px Arial';
  ctx.fillText('\u2713 VERIFIED', stampX, stampY + 2);
  ctx.font = '8px Arial';
  ctx.globalAlpha = 0.6;
  ctx.fillText('FINTECH PLATFORM', stampX, stampY + 18);
  ctx.font = '7px Arial';
  ctx.fillText('Lagos, Nigeria', stampX, stampY + 32);
  ctx.restore();
  // --- Signature ---
  const sigX = 430;
  const sigY = 660;
  ctx.save();
  ctx.strokeStyle = '#1A2E1A';
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.globalAlpha = 0.7;
  ctx.beginPath();
  ctx.moveTo(sigX - 60, sigY + 20);
  ctx.bezierCurveTo(sigX - 40, sigY - 15, sigX - 20, sigY - 20, sigX, sigY + 5);
  ctx.bezierCurveTo(sigX + 15, sigY + 25, sigX + 30, sigY - 10, sigX + 50, sigY);
  ctx.bezierCurveTo(sigX + 60, sigY + 5, sigX + 70, sigY + 15, sigX + 80, sigY + 5);
  ctx.stroke();
  // Underline flourish
  ctx.globalAlpha = 0.4;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(sigX - 40, sigY + 28);
  ctx.bezierCurveTo(sigX - 10, sigY + 22, sigX + 20, sigY + 20, sigX + 60, sigY + 26);
  ctx.stroke();
  ctx.restore();
  // Signature name
  ctx.textAlign = 'center';
  ctx.fillStyle = '#1A2E1A';
  ctx.font = 'bold 12px Arial';
  ctx.fillText(sigName, sigX + 10, sigY + 50);
  ctx.fillStyle = '#6B7280';
  ctx.font = '10px Arial';
  ctx.fillText('Authorized Signatory', sigX + 10, sigY + 65);
  // Signature line
  ctx.strokeStyle = '#d1d5db';
  ctx.lineWidth = 1;
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.moveTo(sigX - 50, sigY + 72);
  ctx.lineTo(sigX + 70, sigY + 72);
  ctx.stroke();
  // Footer
  ctx.textAlign = 'center';
  ctx.fillStyle = '#1A2E1A';
  ctx.font = 'bold 13px Arial';
  ctx.fillText('Thank you for using Billvast.', 300, 810);
  ctx.fillStyle = '#6B7280';
  ctx.font = '12px Arial';
  ctx.fillText('This is a computer-generated receipt.', 300, 832);
  ctx.fillText('Questions? Contact support@billvast.com', 300, 852);
  // Bottom accent bar
  ctx.fillStyle = '#73C883';
  ctx.fillRect(0, 944, 600, 6);
  // Trigger download
  const link = document.createElement('a');
  link.download = 'Billvast-Receipt-' + tx.reference + '.png';
  link.href = canvas.toDataURL('image/png');
  link.click();
};
const generateStatementPDF = (allTransactions: Transaction[], user: User, dateRangeDays: number, periodText: string) => {
  const win = window.open('', '_blank');
  if (!win) {
    toast.error('Please allow popups to generate PDF');
    return;
  }
  const now = new Date();
  let startDate = new Date(0);
  if (dateRangeDays > 0) {
    startDate = new Date();
    startDate.setDate(now.getDate() - dateRangeDays);
  }
  const periodTxs = allTransactions.filter(tx => new Date(tx.date) >= startDate);
  const sortedTxs = [...periodTxs].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const periodNetChange = periodTxs.reduce((sum, tx) => sum + tx.amount, 0);
  const openingBalance = user.walletBalance - periodNetChange;
  const totalCredits = periodTxs.filter(t => t.amount > 0).reduce((s, t) => s + t.amount, 0);
  const totalDebits = Math.abs(periodTxs.filter(t => t.amount < 0).reduce((s, t) => s + t.amount, 0));
  let runningBalance = openingBalance;
  const rowsHtml = sortedTxs.map(tx => {
    runningBalance += tx.amount;
    const debit = tx.amount < 0 ? formatCurrency(Math.abs(tx.amount)) : '-';
    const credit = tx.amount > 0 ? formatCurrency(tx.amount) : '-';
    const debitStyle = tx.amount < 0 ? 'color:#dc2626;' : '';
    const creditStyle = tx.amount > 0 ? 'color:#059669;' : '';
    return ['<tr>', '  <td>' + formatDate(tx.date) + '</td>', '  <td><div style="font-weight:500;color:#1A2E1A">' + tx.title + '</div>', '      <div style="font-size:11px;color:#6B7280">Ref: ' + tx.reference + '</div></td>', '  <td style="text-align:right;' + debitStyle + '">' + debit + '</td>', '  <td style="text-align:right;' + creditStyle + '">' + credit + '</td>', '  <td style="text-align:right;font-weight:600">' + formatCurrency(runningBalance) + '</td>', '</tr>'].join('');
  }).join('');
  const noRowsHtml = '<tr><td colspan="5" style="text-align:center;padding:30px;color:#6B7280">No transactions found for this period.</td></tr>';
  const fromDate = dateRangeDays === 0 ? 'Account Opening' : formatDate(startDate);
  const stampAndSig = getStampAndSignatureHTML();
  const html = ['<!DOCTYPE html>', '<html>', '<head>', '  <title>Account Statement - ' + user.name + '</title>', '  <style>{`', '    @import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap");', '    * { box-sizing: border-box; margin: 0; padding: 0; }', '    body { font-family: "Inter", sans-serif; padding: 40px; color: #1A2E1A; max-width: 900px; margin: 0 auto; background: #fff; }', '    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; border-bottom: 2px solid #006633; padding-bottom: 20px; }', '    .logo { max-width: 150px; margin-bottom: 10px; }', '    .company-info { font-size: 13px; color: #6B7280; line-height: 1.5; }', '    .statement-title { text-align: right; }', '    .statement-title h1 { font-size: 28px; color: #006633; text-transform: uppercase; margin-bottom: 4px; }', '    .statement-title p { color: #6B7280; font-size: 14px; }', '    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 40px; }', '    .info-box { background: #f9fafb; padding: 20px; border-radius: 8px; border: 1px solid #e5e7eb; }', '    .info-box h3 { font-size: 12px; color: #6B7280; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px; }', '    .info-box p { font-weight: 600; font-size: 16px; margin-bottom: 4px; }', '    .info-box .sub { font-weight: 400; color: #6B7280; font-size: 14px; margin-top: 2px; }', '    .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 40px; }', '    .summary-item { padding: 15px; border: 1px solid #e5e7eb; border-radius: 8px; text-align: center; }', '    .summary-item .label { font-size: 11px; color: #6B7280; text-transform: uppercase; margin-bottom: 6px; }', '    .summary-item .value { font-size: 18px; font-weight: 700; }', '    table { width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 40px; }', '    th { background-color: #f3f4f6; color: #4b5563; font-weight: 600; text-align: left; padding: 12px; border-bottom: 2px solid #d1d5db; text-transform: uppercase; font-size: 11px; }', '    td { padding: 12px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }', '    .footer { text-align: center; color: #6B7280; font-size: 12px; margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb; }', '    @media print { body { padding: 20px; } .info-box { background: transparent; border: 1px solid #000; } th { background-color: transparent; border-bottom: 2px solid #000; color: #000; } td { border-bottom: 1px solid #ccc; } * { -webkit-print-color-adjust: exact; } }', '`}</style>', '</head>', '<body>', '  <div class="header">', '    <div>', '      <img src="https://cdn.magicpatterns.com/uploads/4AEfCpc54BiEMtMYwDkd9o/Billvast_logo.png" class="logo" alt="Billvast" />', '      <div class="company-info">123 Financial District, Victoria Island<br/>Lagos, Nigeria<br/>support@billvast.com | +234 800 BILLVAST</div>', '    </div>', '    <div class="statement-title">', '      <h1>Account Statement</h1>', '      <p>Generated on ' + formatDate(now) + '</p>', '    </div>', '  </div>', '  <div class="info-grid">', '    <div class="info-box">', '      <h3>Account Details</h3>', '      <p>' + user.name + '</p>', '      <div class="sub">' + user.email + '</div>', '      <div class="sub">' + user.phone + '</div>', '    </div>', '    <div class="info-box">', '      <h3>Statement Period</h3>', '      <p>' + periodText + '</p>', '      <div class="sub">From: ' + fromDate + '</div>', '      <div class="sub">To: ' + formatDate(now) + '</div>', '    </div>', '  </div>', '  <div class="summary-grid">', '    <div class="summary-item"><div class="label">Opening Balance</div><div class="value">' + formatCurrency(openingBalance) + '</div></div>', '    <div class="summary-item"><div class="label">Total Credits</div><div class="value" style="color:#059669">+' + formatCurrency(totalCredits) + '</div></div>', '    <div class="summary-item"><div class="label">Total Debits</div><div class="value" style="color:#dc2626">-' + formatCurrency(totalDebits) + '</div></div>', '    <div class="summary-item" style="background:#f0fdf4;border-color:#bbf7d0"><div class="label">Closing Balance</div><div class="value" style="color:#059669">' + formatCurrency(user.walletBalance) + '</div></div>', '  </div>', '  <table>', '    <thead><tr><th width="15%">Date</th><th width="40%">Description</th><th width="15%" style="text-align:right">Debit</th><th width="15%" style="text-align:right">Credit</th><th width="15%" style="text-align:right">Balance</th></tr></thead>', '    <tbody>' + (rowsHtml || noRowsHtml) + '</tbody>', '  </table>', stampAndSig, '  <div class="footer">', '    This is a computer-generated statement and does not require a signature.<br/>', '    Please report any discrepancies within 14 days of receipt.', '  </div>', '  <script>window.onload = function() { setTimeout(function() { window.print(); }, 500); }<\/script>', '</body>', '</html>'].join('\n');
  win.document.write(html);
  win.document.close();
};
// --- Main Component ---
export const Wallet: React.FC = () => {
  const {
    state,
    dispatch
  } = useAppContext();
  // UI States
  const [activeTab, setActiveTab] = useState<'all' | 'in' | 'out'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  // Modal States
  const [isAddFundsOpen, setIsAddFundsOpen] = useState(false);
  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false);
  const [receiptTransaction, setReceiptTransaction] = useState<Transaction | null>(null);
  const [isStatementModalOpen, setIsStatementModalOpen] = useState(false);
  // Form States
  const [fundAmount, setFundAmount] = useState('');
  const [fundPaymentMethod, setFundPaymentMethod] = useState<'card' | 'google_pay' | 'apple_pay' | 'mobile_pay' | 'ussd' | 'wallet'>('card');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [statementRange, setStatementRange] = useState<number>(30); // days
  // Filter transactions
  const filteredTransactions = useMemo(() => {
    return state.transactions.filter(tx => {
      const matchesTab = activeTab === 'all' ? true : activeTab === 'in' ? tx.amount > 0 : tx.amount < 0;
      const matchesSearch = tx.title.toLowerCase().includes(searchQuery.toLowerCase()) || tx.reference.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = typeFilter === 'All' ? true : tx.type === typeFilter;
      return matchesTab && matchesSearch && matchesType;
    });
  }, [state.transactions, activeTab, searchQuery, typeFilter]);
  // Chart Data (Spending by Category)
  const spendingByCategory = useMemo(() => {
    return state.transactions.filter(tx => tx.amount < 0).reduce((acc, tx) => {
      acc[tx.type] = (acc[tx.type] || 0) + Math.abs(tx.amount);
      return acc;
    }, {} as Record<string, number>);
  }, [state.transactions]);
  const chartData = Object.entries(spendingByCategory).map(([name, value]) => ({
    name,
    value
  }));
  const totalSpent = chartData.reduce((sum, item) => sum + item.value, 0);
  // Handlers
  const handleAddFunds = (e: React.FormEvent) => {
    e.preventDefault();
    const amount = Number(fundAmount);
    if (amount < 1000) {
      toast.error('Minimum amount is ₦1,000');
      return;
    }
    const newTx: Transaction = {
      id: `tx_${Date.now()}`,
      type: 'Wallet',
      title: 'Card Funding',
      amount: amount,
      status: 'Completed',
      date: new Date().toISOString(),
      reference: `FND-${Date.now().toString().slice(-6)}`
    };
    dispatch({
      type: 'ADD_FUNDS',
      payload: amount
    });
    dispatch({
      type: 'ADD_TRANSACTION',
      payload: newTx
    });
    toast.success(`Successfully added ${formatCurrency(amount)}`);
    setIsAddFundsOpen(false);
    setFundAmount('');
    // Automatically show receipt
    setTimeout(() => setReceiptTransaction(newTx), 300);
  };
  const handleWithdraw = (e: React.FormEvent) => {
    e.preventDefault();
    const amount = Number(withdrawAmount);
    if (amount < 1000) {
      toast.error('Minimum withdrawal is ₦1,000');
      return;
    }
    if (amount > (state.user?.walletBalance || 0)) {
      toast.error('Insufficient wallet balance');
      return;
    }
    if (!bankAccount) {
      toast.error('Please select a bank account');
      return;
    }
    const newTx: Transaction = {
      id: `tx_${Date.now()}`,
      type: 'Wallet',
      title: `Withdrawal to ${bankAccount.split(' - ')[0]}`,
      amount: -amount,
      status: 'Completed',
      date: new Date().toISOString(),
      reference: `WDL-${Date.now().toString().slice(-6)}`
    };
    dispatch({
      type: 'DEDUCT_FUNDS',
      payload: amount
    });
    dispatch({
      type: 'ADD_TRANSACTION',
      payload: newTx
    });
    toast.success(`Successfully withdrew ${formatCurrency(amount)}`);
    setIsWithdrawOpen(false);
    setWithdrawAmount('');
    setBankAccount('');
    // Automatically show receipt
    setTimeout(() => setReceiptTransaction(newTx), 300);
  };
  const handleGenerateStatement = (e: React.FormEvent) => {
    e.preventDefault();
    if (!state.user) return;
    const periodMap: Record<number, string> = {
      7: 'Last 7 Days',
      30: 'Last 30 Days',
      90: 'Last 3 Months',
      0: 'All Time'
    };
    generateStatementPDF(state.transactions, state.user, statementRange, periodMap[statementRange]);
    setIsStatementModalOpen(false);
    toast.success('Statement generated successfully');
  };
  // Custom Chart Label
  const renderCustomizedLabel = (props: any) => {
    const {
      cx,
      cy
    } = props.viewBox;
    return <text x={cx} y={cy} fill="#1A2E1A" textAnchor="middle" dominantBaseline="central" data-id="element-2083">
        <tspan x={cx} y={cy - 10} fontSize="13" fill="#6B7280" fontWeight="500" data-id="element-2084">
          Total Spent
        </tspan>
        <tspan x={cx} y={cy + 15} fontSize="20" fontWeight="bold" data-id="element-2085">
          {formatCurrency(totalSpent)}
        </tspan>
      </text>;
  };
  return <div className="space-y-8 max-w-7xl mx-auto pb-12" data-id="element-2086">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-2087">
        <div data-id="element-2088">
          <h1 className="text-3xl font-bold text-brand-text-dark tracking-tight" data-id="element-2089">
            Wallet
          </h1>
          <p className="text-brand-text-light mt-1" data-id="element-2090">
            Manage your funds, view transactions, and generate statements.
          </p>
        </div>
      </div>

      {/* Top Section: Balance & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-id="element-2091">
        {/* Modern Balance Card */}
        <div className="lg:col-span-7 relative bg-gradient-to-br from-[#004d26] via-[#006633] to-[#0A1F0A] rounded-[2rem] p-8 text-white overflow-hidden shadow-xl shadow-brand-primary-dark/20 flex flex-col justify-between min-h-[240px]" data-id="element-2092">
          {/* Abstract Mesh/Glow Effects */}
          <div className="absolute top-0 right-0 w-80 h-80 bg-brand-primary/20 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/3 pointer-events-none" data-id="element-2093" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-accent/10 rounded-full blur-[60px] translate-y-1/3 -translate-x-1/4 pointer-events-none" data-id="element-2094" />

          {/* Watermark Logo */}
          <div className="absolute -right-4 -bottom-4 opacity-5 pointer-events-none" data-id="element-2095">
            <img src="https://cdn.magicpatterns.com/uploads/4AEfCpc54BiEMtMYwDkd9o/Billvast_logo.png" alt="" className="w-64 h-64 grayscale" data-id="element-2096" />
          </div>

          <div className="relative z-10" data-id="element-2097">
            <div className="flex justify-between items-start mb-2" data-id="element-2098">
              <p className="text-brand-primary-light/80 font-medium tracking-wide uppercase text-sm" data-id="element-2099">
                Available Balance
              </p>
              <div className="px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-xs font-medium border border-white/10 flex items-center gap-1.5" data-id="element-2100">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-primary-light animate-pulse" data-id="element-2101"></span>
                Active
              </div>
            </div>

            <h2 className="text-5xl sm:text-6xl font-bold tracking-tight mb-8 text-white drop-shadow-md" data-id="element-2102">
              {formatCurrency(state.user?.walletBalance || 0)}
            </h2>

            <div className="flex items-center gap-6 text-sm font-medium" data-id="element-2103">
              <div className="flex items-center gap-2.5 bg-white/10 px-4 py-2 rounded-xl backdrop-blur-md border border-white/5" data-id="element-2104">
                <div className="w-6 h-6 rounded-full bg-green-400/20 flex items-center justify-center" data-id="element-2105">
                  <ArrowDownRight size={14} className="text-green-300" data-id="element-2106" />
                </div>
                <div data-id="element-2107">
                  <div className="text-white/60 text-[10px] uppercase tracking-wider mb-0.5" data-id="element-2108">
                    Total In
                  </div>
                  <div className="text-white" data-id="element-2109">
                    {formatCurrency(state.transactions.filter(t => t.amount > 0).reduce((s, t) => s + t.amount, 0))}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2.5 bg-white/10 px-4 py-2 rounded-xl backdrop-blur-md border border-white/5" data-id="element-2110">
                <div className="w-6 h-6 rounded-full bg-red-400/20 flex items-center justify-center" data-id="element-2111">
                  <ArrowUpRight size={14} className="text-red-300" data-id="element-2112" />
                </div>
                <div data-id="element-2113">
                  <div className="text-white/60 text-[10px] uppercase tracking-wider mb-0.5" data-id="element-2114">
                    Total Out
                  </div>
                  <div className="text-white" data-id="element-2115">
                    {formatCurrency(Math.abs(state.transactions.filter(t => t.amount < 0).reduce((s, t) => s + t.amount, 0)))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="lg:col-span-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-4" data-id="element-2116">
          <motion.button whileHover={{
          y: -4,
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} onClick={() => setIsAddFundsOpen(true)} className="flex flex-col items-center justify-center p-6 rounded-[2rem] border border-gray-100 bg-white shadow-sm hover:shadow-md transition-all gap-4 group" data-id="element-2117">
            <div className="w-14 h-14 rounded-2xl bg-green-50 text-brand-primary-dark flex items-center justify-center group-hover:bg-brand-primary group-hover:text-white transition-colors" data-id="element-2118">
              <Plus size={28} data-id="element-2119" />
            </div>
            <span className="font-semibold text-brand-text-dark" data-id="element-2120">
              Add Funds
            </span>
          </motion.button>

          <motion.button whileHover={{
          y: -4,
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} onClick={() => setIsWithdrawOpen(true)} className="flex flex-col items-center justify-center p-6 rounded-[2rem] border border-gray-100 bg-white shadow-sm hover:shadow-md transition-all gap-4 group" data-id="element-2121">
            <div className="w-14 h-14 rounded-2xl bg-gray-50 text-gray-700 flex items-center justify-center group-hover:bg-gray-800 group-hover:text-white transition-colors" data-id="element-2122">
              <ArrowUpRight size={28} data-id="element-2123" />
            </div>
            <span className="font-semibold text-brand-text-dark" data-id="element-2124">Withdraw</span>
          </motion.button>

          <motion.button whileHover={{
          y: -4,
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} onClick={() => setIsStatementModalOpen(true)} className="flex flex-col items-center justify-center p-6 rounded-[2rem] border border-gray-100 bg-white shadow-sm hover:shadow-md transition-all gap-4 group col-span-2 sm:col-span-1 lg:col-span-2" data-id="element-2125">
            <div className="w-14 h-14 rounded-2xl bg-orange-50 text-brand-accent flex items-center justify-center group-hover:bg-brand-accent group-hover:text-white transition-colors" data-id="element-2126">
              <FileText size={28} data-id="element-2127" />
            </div>
            <span className="font-semibold text-brand-text-dark" data-id="element-2128">
              Download Statement
            </span>
          </motion.button>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-2129">
        {/* Transaction List */}
        <div className="lg:col-span-2 space-y-6" data-id="element-2130">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-2131">
            <h3 className="text-xl font-bold text-brand-text-dark" data-id="element-2132">
              Recent Transactions
            </h3>

            <div className="flex gap-2" data-id="element-2133">
              <div className="relative" data-id="element-2134">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} data-id="element-2135" />
                <input type="text" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary w-full sm:w-48 transition-all shadow-sm" data-id="element-2136" />
              </div>
              <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none shadow-sm" data-id="element-2137">
                <option value="All" data-id="element-2138">All Types</option>
                <option value="Wallet" data-id="element-2139">Wallet</option>
                <option value="Diaspora" data-id="element-2140">Diaspora</option>
                <option value="Bills" data-id="element-2141">Bills</option>
                <option value="Crowdfunding" data-id="element-2142">Crowdfunding</option>
                <option value="Facility" data-id="element-2143">Facility</option>
              </select>
            </div>
          </div>

          {/* Animated Tabs */}
          <div className="flex space-x-2 bg-gray-100/50 p-1 rounded-xl w-fit" data-id="element-2144">
            {(['all', 'in', 'out'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`relative px-5 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${activeTab === tab ? 'text-brand-text-dark' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-2145">
                {activeTab === tab && <motion.div layoutId="wallet-tab-indicator" className="absolute inset-0 bg-white rounded-lg shadow-sm" initial={false} transition={{
              type: 'spring',
              bounce: 0.2,
              duration: 0.6
            }} data-id="element-2146" />}
                <span className="relative z-10" data-id="element-2147">
                  {tab === 'all' ? 'All' : tab === 'in' ? 'Money In' : 'Money Out'}
                </span>
              </button>)}
          </div>

          {/* Transaction Cards */}
          {filteredTransactions.length > 0 ? <motion.div className="space-y-3" initial="hidden" animate="visible" variants={{
          visible: {
            transition: {
              staggerChildren: 0.05
            }
          }
        }} data-id="element-2148">
              {filteredTransactions.map(tx => <motion.div key={tx.id} variants={{
            hidden: {
              opacity: 0,
              y: 10
            },
            visible: {
              opacity: 1,
              y: 0
            }
          }} className="flex items-center justify-between p-4 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group" data-id="element-2149">
                  <div className="flex items-center gap-4" data-id="element-2150">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${tx.amount > 0 ? 'bg-green-50 text-green-600' : 'bg-gray-50 text-gray-600'}`} data-id="element-2151">
                      {tx.amount > 0 ? <ArrowDownRight size={20} data-id="element-2152" /> : <ArrowUpRight size={20} data-id="element-2153" />}
                    </div>
                    <div data-id="element-2154">
                      <h4 className="font-semibold text-brand-text-dark" data-id="element-2155">
                        {tx.title}
                      </h4>
                      <div className="flex items-center gap-2 mt-1" data-id="element-2156">
                        <span className="text-[11px] font-medium px-2 py-0.5 bg-gray-100 text-gray-600 rounded-md" data-id="element-2157">
                          {tx.type}
                        </span>
                        <span className="text-xs text-brand-text-light" data-id="element-2158">
                          {formatDate(tx.date)}
                        </span>
                        <span className="text-xs text-gray-400 hidden sm:inline-block" data-id="element-2159">
                          • Ref: {tx.reference}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 sm:gap-6" data-id="element-2160">
                    <div className="text-right" data-id="element-2161">
                      <p className={`font-bold ${tx.amount > 0 ? 'text-green-600' : 'text-brand-text-dark'}`} data-id="element-2162">
                        {tx.amount > 0 ? '+' : ''}
                        {formatCurrency(tx.amount)}
                      </p>
                      <div className="mt-1 flex justify-end" data-id="element-2163">
                        <StatusBadge status={tx.status} data-id="element-2164" />
                      </div>
                    </div>

                    {/* Receipt Button (Only for completed) */}
                    {tx.status.toLowerCase() === 'completed' && <button onClick={() => setReceiptTransaction(tx)} className="p-2.5 text-gray-400 hover:text-brand-primary hover:bg-brand-primary/10 rounded-full transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100 shrink-0" title="View Receipt" data-id="element-2165">
                        <Receipt size={20} data-id="element-2166" />
                      </button>}
                  </div>
                </motion.div>)}
            </motion.div> : <div className="py-12" data-id="element-2167">
              <EmptyState icon={Search} title="No transactions found" description="Try adjusting your filters or search query." data-id="element-2168" />
            </div>}
        </div>

        {/* Spending Chart Sidebar */}
        <div className="space-y-6" data-id="element-2169">
          <h3 className="text-xl font-bold text-brand-text-dark" data-id="element-2170">Analytics</h3>
          <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm p-6 flex flex-col h-[400px]" data-id="element-2171">
            <h4 className="font-semibold text-brand-text-dark mb-6" data-id="element-2172">
              Spending by Category
            </h4>
            {chartData.length > 0 ? <div className="flex-1 relative" data-id="element-2173">
                <ResponsiveContainer width="100%" height="100%" data-id="element-2174">
                  <PieChart data-id="element-2175">
                    <Pie data={chartData} cx="50%" cy="45%" innerRadius={70} outerRadius={90} paddingAngle={5} dataKey="value" stroke="none" data-id="element-2176">
                      {chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-2177" />)}
                    </Pie>
                    <Tooltip formatter={(value: number) => formatCurrency(value)} contentStyle={{
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                }} data-id="element-2178" />
                    <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{
                  fontSize: '12px'
                }} data-id="element-2179" />
                  </PieChart>
                </ResponsiveContainer>
                {/* Custom Center Label Overlay */}
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none pb-8" data-id="element-2180">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2181">
                    Total Spent
                  </span>
                  <span className="text-xl font-bold text-brand-text-dark mt-1" data-id="element-2182">
                    {formatCurrency(totalSpent)}
                  </span>
                </div>
              </div> : <div className="flex-1 flex items-center justify-center text-gray-400 text-sm" data-id="element-2183">
                No spending data yet
              </div>}
          </div>
        </div>
      </div>

      {/* --- Modals --- */}

      {/* Add Funds Modal */}
      <Modal isOpen={isAddFundsOpen} onClose={() => setIsAddFundsOpen(false)} title="Add Funds to Wallet" data-id="element-2184">
        <form onSubmit={handleAddFunds} className="space-y-5" data-id="element-2185">
          <div data-id="element-2186">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2187">
              Amount (₦)
            </label>
            <div className="relative" data-id="element-2188">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold" data-id="element-2189">
                ₦
              </span>
              <input type="number" value={fundAmount} onChange={e => setFundAmount(e.target.value)} placeholder="0.00" className="w-full pl-10 pr-4 py-3.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none text-lg font-semibold transition-all" min="1000" required data-id="element-2190" />
            </div>
            <p className="text-xs text-gray-500 mt-2" data-id="element-2191">
              Minimum amount is ₦1,000
            </p>
          </div>

          <div data-id="element-2192">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2193">
              Payment Method
            </label>
            <div className="grid grid-cols-3 gap-2 mb-4" data-id="element-2194">
              <button type="button" onClick={() => setFundPaymentMethod('card')} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${fundPaymentMethod === 'card' ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-100 hover:border-gray-200 text-gray-500'}`} data-id="element-2195">
                <CreditCard size={20} data-id="element-2196" />
                <span className="text-xs font-semibold" data-id="element-2197">Card</span>
              </button>
              <button type="button" onClick={() => setFundPaymentMethod('ussd')} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${fundPaymentMethod === 'ussd' ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-100 hover:border-gray-200 text-gray-500'}`} data-id="element-2198">
                <Hash size={20} data-id="element-2199" />
                <span className="text-xs font-semibold" data-id="element-2200">USSD</span>
              </button>
              <button type="button" onClick={() => setFundPaymentMethod('wallet')} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${fundPaymentMethod === 'wallet' ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-100 hover:border-gray-200 text-gray-500'}`} data-id="element-2201">
                <WalletIcon size={20} data-id="element-2202" />
                <span className="text-xs font-semibold" data-id="element-2203">Wallet</span>
              </button>
              <button type="button" onClick={() => setFundPaymentMethod('google_pay')} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${fundPaymentMethod === 'google_pay' ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-100 hover:border-gray-200 text-gray-500'}`} data-id="element-2204">
                <div className="flex items-center gap-1 font-bold text-gray-800 text-sm" data-id="element-2205">
                  <span className="text-blue-500" data-id="element-2206">G</span>Pay
                </div>
                <span className="text-xs font-semibold" data-id="element-2207">Google Pay</span>
              </button>
              <button type="button" onClick={() => setFundPaymentMethod('apple_pay')} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${fundPaymentMethod === 'apple_pay' ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-100 hover:border-gray-200 text-gray-500'}`} data-id="element-2208">
                <div className="font-bold text-gray-800 text-sm flex items-center" data-id="element-2209">
                   Pay
                </div>
                <span className="text-xs font-semibold" data-id="element-2210">Apple Pay</span>
              </button>
              <button type="button" onClick={() => setFundPaymentMethod('mobile_pay')} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${fundPaymentMethod === 'mobile_pay' ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-100 hover:border-gray-200 text-gray-500'}`} data-id="element-2211">
                <Smartphone size={20} data-id="element-2212" />
                <span className="text-xs font-semibold" data-id="element-2213">Mobile Pay</span>
              </button>
            </div>

            {fundPaymentMethod === 'card' ? <div className="space-y-3 animate-fade-in" data-id="element-2214">
                <div className="flex items-center gap-2 mb-2" data-id="element-2215">
                  {/* Visa */}
                  <div className="h-6 px-2 bg-blue-50 rounded flex items-center justify-center text-blue-800 font-bold italic text-xs border border-blue-100" data-id="element-2216">
                    VISA
                  </div>
                  {/* Mastercard */}
                  <div className="h-6 px-2 bg-gray-50 rounded flex items-center justify-center border border-gray-200" data-id="element-2217">
                    <div className="flex -space-x-1.5" data-id="element-2218">
                      <div className="w-3 h-3 rounded-full bg-red-500 mix-blend-multiply" data-id="element-2219"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500 mix-blend-multiply" data-id="element-2220"></div>
                    </div>
                  </div>
                  {/* Amex */}
                  <div className="h-6 px-2 bg-blue-600 rounded flex items-center justify-center text-white font-bold text-[10px] tracking-tighter" data-id="element-2221">
                    AMEX
                  </div>
                  {/* Verve */}
                  <div className="h-6 px-2 bg-red-600 rounded flex items-center justify-center text-white font-bold italic text-[10px]" data-id="element-2222">
                    Verve
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl shadow-sm" data-id="element-2223">
                  <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center text-gray-600" data-id="element-2224">
                    <CreditCard size={20} data-id="element-2225" />
                  </div>
                  <div className="flex-1" data-id="element-2226">
                    <p className="text-sm font-semibold text-brand-text-dark" data-id="element-2227">
                      Saved Debit Card
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-2228">•••• 4242</p>
                  </div>
                  <CheckCircle2 className="text-brand-primary" size={20} data-id="element-2229" />
                </div>
              </div> : fundPaymentMethod === 'ussd' ? <div className="space-y-4 animate-fade-in" data-id="element-2230">
                <div data-id="element-2231">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2232">
                    Select Bank for USSD
                  </label>
                  <select className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-brand-primary outline-none bg-white" data-id="element-2233">
                    <option value="*737#" data-id="element-2234">GTBank (*737#)</option>
                    <option value="*901#" data-id="element-2235">Access Bank (*901#)</option>
                    <option value="*770#" data-id="element-2236">Fidelity Bank (*770#)</option>
                    <option value="*894#" data-id="element-2237">First Bank (*894#)</option>
                    <option value="*822#" data-id="element-2238">Sterling Bank (*822#)</option>
                    <option value="*919#" data-id="element-2239">UBA (*919#)</option>
                    <option value="*966#" data-id="element-2240">Zenith Bank (*966#)</option>
                  </select>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 text-center" data-id="element-2241">
                  <p className="text-sm text-gray-600 mb-2" data-id="element-2242">
                    Dial the code below on your mobile phone to complete the
                    payment.
                  </p>
                  <div className="text-2xl font-bold text-brand-text-dark tracking-wider" data-id="element-2243">
                    *737*000*1234#
                  </div>
                </div>
              </div> : fundPaymentMethod === 'wallet' ? <div className="space-y-4 animate-fade-in" data-id="element-2244">
                <div className="bg-brand-primary-dark/5 p-4 rounded-xl border border-brand-primary/20 text-center" data-id="element-2245">
                  <p className="text-sm text-gray-600 mb-1" data-id="element-2246">
                    Current Wallet Balance
                  </p>
                  <div className="text-2xl font-bold text-brand-primary-dark" data-id="element-2247">
                    {formatCurrency(state.user?.walletBalance || 0)}
                  </div>
                  <p className="text-xs text-brand-primary mt-2 flex items-center justify-center gap-1" data-id="element-2248">
                    <CheckCircle2 size={14} data-id="element-2249" /> Sufficient balance available
                  </p>
                </div>
              </div> : <div className="space-y-4 animate-fade-in" data-id="element-2250">
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 text-center" data-id="element-2251">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm" data-id="element-2252">
                    {fundPaymentMethod === 'google_pay' && <span className="font-bold text-xl" data-id="element-2253">
                        <span className="text-blue-500" data-id="element-2254">G</span>Pay
                      </span>}
                    {fundPaymentMethod === 'apple_pay' && <span className="font-bold text-2xl" data-id="element-2255"></span>}
                    {fundPaymentMethod === 'mobile_pay' && <Smartphone size={32} className="text-gray-700" data-id="element-2256" />}
                  </div>
                  <h4 className="font-semibold text-brand-text-dark mb-2" data-id="element-2257">
                    Pay with{' '}
                    {fundPaymentMethod.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-2258">
                    You will be redirected to complete your{' '}
                    {fundAmount ? formatCurrency(Number(fundAmount)) : 'payment'}{' '}
                    securely.
                  </p>
                </div>
              </div>}
          </div>

          <button type="submit" className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white py-4 rounded-xl font-semibold transition-colors mt-2 shadow-md shadow-brand-primary/20" data-id="element-2259">
            Fund Wallet
          </button>
        </form>
      </Modal>

      {/* Withdraw Modal */}
      <Modal isOpen={isWithdrawOpen} onClose={() => setIsWithdrawOpen(false)} title="Withdraw Funds" data-id="element-2260">
        <form onSubmit={handleWithdraw} className="space-y-5" data-id="element-2261">
          <div className="bg-brand-primary-dark/5 p-4 rounded-xl border border-brand-primary/20 flex justify-between items-center" data-id="element-2262">
            <span className="text-sm font-medium text-brand-text-dark" data-id="element-2263">
              Available Balance
            </span>
            <span className="font-bold text-brand-primary-dark text-lg" data-id="element-2264">
              {formatCurrency(state.user?.walletBalance || 0)}
            </span>
          </div>

          <div data-id="element-2265">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2266">
              Amount (₦)
            </label>
            <div className="relative" data-id="element-2267">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold" data-id="element-2268">
                ₦
              </span>
              <input type="number" value={withdrawAmount} onChange={e => setWithdrawAmount(e.target.value)} placeholder="0.00" className="w-full pl-10 pr-4 py-3.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none text-lg font-semibold transition-all" min="1000" max={state.user?.walletBalance} required data-id="element-2269" />
            </div>
          </div>

          <div data-id="element-2270">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2271">
              Destination Bank
            </label>
            <select required value={bankAccount} onChange={e => setBankAccount(e.target.value)} className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none bg-white font-medium text-gray-700" data-id="element-2272">
              <option value="" disabled data-id="element-2273">
                Select saved account
              </option>
              <option value="GTBank - 0123456789" data-id="element-2274">GTBank - 0123456789</option>
              <option value="Access - 0987654321" data-id="element-2275">Access - 0987654321</option>
            </select>
          </div>

          <button type="submit" className="w-full bg-gray-900 hover:bg-black text-white py-4 rounded-xl font-semibold transition-colors mt-2 shadow-md flex items-center justify-center gap-2" data-id="element-2276">
            <Building size={18} data-id="element-2277" /> Withdraw to Bank
          </button>
        </form>
      </Modal>

      {/* Receipt Modal */}
      <Modal isOpen={!!receiptTransaction} onClose={() => setReceiptTransaction(null)} title="Transaction Receipt" maxWidth="max-w-md" data-id="element-2278">
        {receiptTransaction && <div className="space-y-6" data-id="element-2279">
            {/* Visual Receipt Card */}
            <div className="bg-white border-2 border-dashed border-gray-200 rounded-2xl p-6 relative overflow-hidden" data-id="element-2280">
              {/* Decorative top edge */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-brand-primary" data-id="element-2281"></div>

              <div className="flex justify-center mb-6 mt-2" data-id="element-2282">
                <BillvastLogo size="sm" showText data-id="element-2283" />
              </div>

              <div className="text-center mb-6" data-id="element-2284">
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-2285">
                  Amount
                </p>
                <h3 className={`text-4xl font-bold ${receiptTransaction.amount > 0 ? 'text-brand-primary-dark' : 'text-brand-text-dark'}`} data-id="element-2286">
                  {receiptTransaction.amount > 0 ? '+' : ''}
                  {formatCurrency(receiptTransaction.amount)}
                </h3>
              </div>

              <div className="space-y-4 text-sm" data-id="element-2287">
                <div className="flex justify-between border-b border-gray-100 pb-3" data-id="element-2288">
                  <span className="text-gray-500" data-id="element-2289">Type</span>
                  <span className="font-medium text-brand-text-dark" data-id="element-2290">
                    {receiptTransaction.type}
                  </span>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-3" data-id="element-2291">
                  <span className="text-gray-500" data-id="element-2292">Description</span>
                  <span className="font-medium text-brand-text-dark text-right max-w-[60%]" data-id="element-2293">
                    {receiptTransaction.title}
                  </span>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-3" data-id="element-2294">
                  <span className="text-gray-500" data-id="element-2295">Date</span>
                  <span className="font-medium text-brand-text-dark" data-id="element-2296">
                    {formatDateTime(receiptTransaction.date)}
                  </span>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-3" data-id="element-2297">
                  <span className="text-gray-500" data-id="element-2298">Reference</span>
                  <span className="font-mono text-xs font-medium text-brand-text-dark" data-id="element-2299">
                    {receiptTransaction.reference}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-1" data-id="element-2300">
                  <span className="text-gray-500" data-id="element-2301">Status</span>
                  <span className="flex items-center gap-1.5 text-green-600 font-semibold bg-green-50 px-2.5 py-1 rounded-md" data-id="element-2302">
                    <CheckCircle2 size={14} data-id="element-2303" /> {receiptTransaction.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Download Actions */}
            <div className="grid grid-cols-2 gap-3" data-id="element-2304">
              <button onClick={() => generateReceiptPNG(receiptTransaction)} className="flex items-center justify-center gap-2 py-3 rounded-xl border border-gray-200 font-semibold text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-2305">
                <Download size={18} data-id="element-2306" /> PNG
              </button>
              <button onClick={() => generateReceiptPDF(receiptTransaction)} className="flex items-center justify-center gap-2 py-3 rounded-xl bg-brand-primary hover:bg-brand-primary-dark text-white font-semibold transition-colors shadow-sm" data-id="element-2307">
                <FileText size={18} data-id="element-2308" /> PDF
              </button>
            </div>
          </div>}
      </Modal>

      {/* Statement Modal */}
      <Modal isOpen={isStatementModalOpen} onClose={() => setIsStatementModalOpen(false)} title="Generate Statement" data-id="element-2309">
        <form onSubmit={handleGenerateStatement} className="space-y-6" data-id="element-2310">
          <div className="bg-orange-50 p-4 rounded-xl flex items-start gap-3" data-id="element-2311">
            <FileText className="text-brand-accent shrink-0 mt-0.5" size={20} data-id="element-2312" />
            <p className="text-sm text-brand-text-dark" data-id="element-2313">
              Generate a formal PDF account statement. This document can be used
              for official financial verification.
            </p>
          </div>

          <div data-id="element-2314">
            <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-2315">
              Select Date Range
            </label>
            <div className="grid grid-cols-2 gap-3" data-id="element-2316">
              {[{
              label: 'Last 7 Days',
              value: 7
            }, {
              label: 'Last 30 Days',
              value: 30
            }, {
              label: 'Last 3 Months',
              value: 90
            }, {
              label: 'All Time',
              value: 0
            }].map(option => <button key={option.value} type="button" onClick={() => setStatementRange(option.value)} className={`py-3 px-4 rounded-xl border text-sm font-medium transition-all flex items-center gap-2 ${statementRange === option.value ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`} data-id="element-2317">
                  <Calendar size={16} className={statementRange === option.value ? 'text-brand-primary' : 'text-gray-400'} data-id="element-2318" />
                  {option.label}
                </button>)}
            </div>
          </div>

          <button type="submit" className="w-full bg-brand-text-dark hover:bg-black text-white py-4 rounded-xl font-semibold transition-colors shadow-md flex items-center justify-center gap-2" data-id="element-2319">
            <Download size={18} data-id="element-2320" /> Generate PDF Statement
          </button>
        </form>
      </Modal>
    </div>;
};